/**
 * API Error handling utilities
 */

export interface APIError {
  message: string;
  status?: number;
  code?: string;
  details?: any;
}

export class TEVAPIError extends Error {
  public status?: number;
  public code?: string;
  public details?: any;

  constructor(error: APIError) {
    super(error.message);
    this.name = 'TEVAPIError';
    this.status = error.status;
    this.code = error.code;
    this.details = error.details;
  }
}

/**
 * Extract error message from various error response formats
 */
export const extractErrorMessage = (error: any): string => {
  // Check for message in various places
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.response?.data?.detail) {
    return error.response.data.detail;
  }
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  if (error.message) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }

  // Default message based on status
  if (error.response?.status) {
    switch (error.response.status) {
      case 400:
        return 'Bad request. Please check your input.';
      case 401:
        return 'You are not authenticated. Please log in.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 500:
        return 'Internal server error. Please try again later.';
      default:
        return `Server error (${error.response.status})`;
    }
  }

  return 'An unexpected error occurred';
};

/**
 * Create a standardized error object from various error formats
 */
export const normalizeError = (error: any): TEVAPIError => {
  const message = extractErrorMessage(error);
  const status = error.response?.status;
  const code = error.response?.data?.code || error.code;
  const details = error.response?.data;

  return new TEVAPIError({ message, status, code, details });
};

/**
 * Log error for debugging
 */
export const logError = (error: any, context?: string): void => {
  const timestamp = new Date().toISOString();
  const normalizedError = normalizeError(error);

  console.error(`[${timestamp}] ${context || 'API Error'}:`, {
    message: normalizedError.message,
    status: normalizedError.status,
    code: normalizedError.code,
    details: normalizedError.details,
    originalError: error,
  });
};
